import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { Box, Button, Modal, Typography } from '@mui/material'
import { Spinner, Table } from 'reactstrap'
import { currencyFormatter } from '../../../utils/currencyFormatter'
import { notifyError, notifySuccess } from '../../../utils/notify'
import {
  aplicarSuplementacao,
  computeDeficit,
  fetchItensOrcamentoMes,
  labelMes,
  mesesSeguintesNoAno,
} from './borderoSuplementacao'

function fmtDate(value) {
  if (!value) return '—'
  const parsed = moment(value)
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '—'
}

const ModalOrcamentoExcedido = ({
  open,
  onClose,
  onCadastrarPendente,
  onAprovarCadastro,
  isGestor,
  isLoadingCadastra,
  orcamentoInfoMes = {},
  valor,
  orcItemId,
  orcItemLabel,
  dataVenc,
  currentBdroId,
  currentDesc,
  currentFavLabel,
  userPfId,
}) => {
  const [linhas, setLinhas] = useState([])
  const [loadingItens, setLoadingItens] = useState(false)
  const [approving, setApproving] = useState(false)

  const yyyyMm = dataVenc ? moment(dataVenc).format('YYYY-MM') : ''
  const mesLabel = labelMes(yyyyMm)
  const deficit = computeDeficit(
    orcamentoInfoMes?.emOrcDot,
    orcamentoInfoMes?.jaNoBordero,
    valor,
  )
  const saldo = Number(orcamentoInfoMes?.emOrcDot || 0)
    - Number(orcamentoInfoMes?.jaNoBordero || 0)
    - Number(valor || 0)
  const proximosMeses = mesesSeguintesNoAno(yyyyMm)

  useEffect(() => {
    if (!open || !isGestor || !orcItemId || !yyyyMm) {
      setLinhas([])
      return undefined
    }

    let cancelled = false
    setLoadingItens(true)
    fetchItensOrcamentoMes({ orcItemId, yyyyMm })
      .then(({ linhas: fetched }) => {
        if (cancelled) return
        const currentId = Number(currentBdroId) || 0
        const mapped = (fetched || []).map((row) => (
          currentId && Number(row.bdroId) === currentId
            ? { ...row, destaque: true, origem: 'Esta solicitação' }
            : row
        ))
        const alreadyInList = currentId && mapped.some((row) => Number(row.bdroId) === currentId)
        if (!alreadyInList) {
          mapped.unshift({
            key: 'current',
            origem: 'Esta solicitação',
            bdroId: currentId || null,
            data: dataVenc,
            descricao: currentDesc || '—',
            favorecido: currentFavLabel || '—',
            valor: Math.abs(Number(valor || 0)),
            status: 'Atual',
            destaque: true,
          })
        }
        setLinhas(mapped)
      })
      .catch(() => {
        if (cancelled) return
        setLinhas([{
          key: 'current',
          origem: 'Esta solicitação',
          bdroId: Number(currentBdroId) || null,
          data: dataVenc,
          descricao: currentDesc || '—',
          favorecido: currentFavLabel || '—',
          valor: Math.abs(Number(valor || 0)),
          status: 'Atual',
          destaque: true,
        }])
      })
      .finally(() => {
        if (!cancelled) setLoadingItens(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, isGestor, orcItemId, yyyyMm, currentBdroId, currentDesc, currentFavLabel, dataVenc, valor])

  const busy = Boolean(isLoadingCadastra || approving)

  const handleAprovar = async () => {
    setApproving(true)
    try {
      const aplicado = await aplicarSuplementacao({
        orcItemId,
        yyyyMm,
        deficit,
        userPfId,
      })
      notifySuccess(`Suplementação de ${currencyFormatter.format(aplicado)} aplicada.`)
      if (typeof onAprovarCadastro === 'function') {
        await onAprovarCadastro()
      }
    } catch (err) {
      notifyError(err?.message || 'Não foi possível aprovar a suplementação.')
    } finally {
      setApproving(false)
    }
  }

  const avisoMeses = useMemo(() => {
    if (!proximosMeses.length) {
      return 'Não há meses seguintes neste ano para revisar.'
    }
    return `Revise manualmente as dotações de ${proximosMeses.join(', ')} deste item, se necessário.`
  }, [proximosMeses])

  return (
    <Modal
      open={open}
      onClose={busy ? undefined : onClose}
      aria-labelledby="modal-orcamento-excedido-title"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: isGestor ? 920 : 520,
        maxWidth: '96vw',
        maxHeight: '90vh',
        overflow: 'auto',
        bgcolor: 'background.paper',
        border: '1px solid #d1d5db',
        borderRadius: 1,
        boxShadow: 24,
        p: 3,
      }}>
        <Typography id="modal-orcamento-excedido-title" variant="h6" component="h2">
          Orçamento excedido
        </Typography>
        <Typography sx={{ mt: 1.5, fontSize: 14 }}>
          Item: <strong>{orcItemLabel || '—'}</strong><br />
          Mês: <strong>{mesLabel}</strong><br />
          Valor solicitado: {currencyFormatter.format(Number(valor || 0))}<br />
          Valor já solicitado com este item: {currencyFormatter.format(Number(orcamentoInfoMes?.jaNoBordero || 0))}<br />
          Dotação do mês: {currencyFormatter.format(Number(orcamentoInfoMes?.emOrcDot || 0))}<br />
          Saldo da solicitação: {currencyFormatter.format(saldo)}
        </Typography>

        {isGestor ? (
          <>
            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fef3c7', borderRadius: 1 }}>
              <Typography sx={{ fontSize: 14 }}>
                Suplementação a aplicar (fixa no déficit):{' '}
                <strong>{currencyFormatter.format(deficit)}</strong>
              </Typography>
              <Typography sx={{ fontSize: 13, mt: 0.75, color: '#78350f' }}>
                A suplementação vale apenas para {mesLabel}. {avisoMeses}
              </Typography>
            </Box>

            <Typography sx={{ mt: 2, mb: 1, fontSize: 13, fontWeight: 600 }}>
              Lançamentos do mês (borderô e movimentos, sem duplicar os já associados)
            </Typography>
            {loadingItens ? (
              <div className="text-center py-3"><Spinner size="sm" /></div>
            ) : (
              <div style={{ maxHeight: 280, overflow: 'auto' }}>
                <Table size="sm" responsive bordered className="mb-0" style={{ fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th>Origem</th>
                      <th>Data</th>
                      <th>Descrição</th>
                      <th>Favorecido</th>
                      <th>Valor</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-muted">Nenhum lançamento neste mês além desta solicitação.</td>
                      </tr>
                    )}
                    {linhas.map((row) => (
                      <tr
                        key={row.key}
                        style={row.destaque ? { background: '#ecfdf5' } : undefined}
                      >
                        <td>{row.origem}</td>
                        <td>{fmtDate(row.data)}</td>
                        <td>{row.descricao}</td>
                        <td>{row.favorecido}</td>
                        <td>{currencyFormatter.format(row.valor)}</td>
                        <td>{row.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        ) : (
          <Typography sx={{ mt: 2, fontSize: 14 }}>
            Solicite ao seu gerente para avaliar suplementação orçamentária.
          </Typography>
        )}

        <Box sx={{ mt: 2.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button type="button" onClick={onClose} disabled={busy}>Ajustar</Button>
          {isGestor ? (
            <Button
              type="button"
              className="text-success"
              onClick={handleAprovar}
              disabled={busy || !(deficit > 0)}
            >
              {approving ? 'Aguarde' : 'Aprovar suplementação e cadastrar'}
            </Button>
          ) : (
            !isLoadingCadastra
              ? <Button type="button" onClick={onCadastrarPendente}>Cadastrar mesmo assim</Button>
              : (
                <Button type="button" variant="outlined" disabled endIcon={<Spinner />}>
                  Aguarde
                </Button>
              )
          )}
        </Box>
      </Box>
    </Modal>
  )
}

export default ModalOrcamentoExcedido
